import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Pencil, Trash2, Download, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
    ['nom', 'Nom du traitement'], ['finalite', 'Finalité'], ['base_legale', 'Base légale (Art. 6 RGPD)'],
    ['categories_donnees', 'Catégories de données'], ['personnes_concernees', 'Personnes concernées'],
    ['destinataires', 'Destinataires / sous-traitants'], ['duree_conservation', 'Durée de conservation'],
    ['mesures_securite', 'Mesures de sécurité'],
];

export default function GdprRegister({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [items, setItems] = useState(null);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get('/tech/gdpr-register').then(r => setItems(r.data.items)).catch(() => toast.error('Chargement impossible'));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const persist = async (next) => {
        setSaving(true);
        try {
            await api.put('/tech/gdpr-register', { items: next });
            setItems(next);
            toast.success('Registre mis à jour');
        } catch { toast.error('Sauvegarde échouée'); }
        finally { setSaving(false); }
    };

    const saveEntry = () => {
        if (!editing?.nom?.trim()) { toast.error('Nom du traitement requis'); return; }
        const entry = { ...editing, id: editing.id || `traitement-${Date.now()}` };
        const next = items.some(i => i.id === entry.id)
            ? items.map(i => i.id === entry.id ? entry : i)
            : [...items, entry];
        persist(next);
        setEditing(null);
    };

    const remove = (entry) => {
        if (!window.confirm(`Supprimer le traitement « ${entry.nom} » du registre ?`)) return;
        persist(items.filter(i => i.id !== entry.id));
    };

    const exportCsv = () => {
        const rows = [FIELDS.map(([, l]) => l).join(';')];
        items.forEach(i => rows.push(FIELDS.map(([k]) => `"${(i[k] || '').replace(/"/g, '""')}"`).join(';')));
        const blob = new Blob(['\ufeff' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = 'registre_traitements_rgpd.csv'; a.click();
    };

    if (!items) return <div className="flex justify-center py-16" data-testid="gdpr-loading"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-5" data-testid="gdpr-register">
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                <ShieldCheck size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-sm text-emerald-800">
                    Registre tenu conformément à l'<strong>Article 30 du RGPD</strong>. Documentez chaque traitement de données personnelles : finalité, base légale, conservation et mesures de sécurité. Exportable pour la CNIL ou un DPO.
                </p>
            </div>
            <div className="flex items-center gap-2">
                <Button onClick={() => setEditing({})} data-testid="gdpr-add-btn"><Plus size={14} className="mr-1.5" /> Ajouter un traitement</Button>
                <Button variant="outline" onClick={exportCsv} data-testid="gdpr-export-btn"><Download size={14} className="mr-1.5" /> Exporter (CSV)</Button>
                <div className="ml-auto text-sm text-foreground/60">{items.length} traitement{items.length > 1 ? 's' : ''}</div>
            </div>
            <div className="space-y-3">
                {items.map(i => (
                    <div key={i.id} className="rounded-xl border border-border bg-surface p-4" data-testid="gdpr-entry">
                        <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="font-medium">{i.nom}</div>
                                <div className="text-xs text-foreground/60 mt-0.5">{i.finalite}</div>
                                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs">
                                    {FIELDS.slice(2).map(([k, l]) => i[k] && (
                                        <div key={k}><span className="text-foreground/45">{l} :</span> {i[k]}</div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                                <Button size="sm" variant="outline" onClick={() => setEditing(i)} data-testid="gdpr-edit-btn"><Pencil size={13} /></Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => remove(i)} data-testid="gdpr-delete-btn"><Trash2 size={13} /></Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={!!editing} onOpenChange={v => !v && setEditing(null)}>
                <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="gdpr-form">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{editing?.id ? 'Modifier le traitement' : 'Nouveau traitement'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        {FIELDS.map(([k, l]) => (
                            <div key={k}>
                                <label className="text-xs font-medium text-foreground/70 block mb-1">{l}</label>
                                {['finalite', 'categories_donnees', 'mesures_securite'].includes(k)
                                    ? <Textarea rows={2} value={editing?.[k] || ''} onChange={e => setEditing(x => ({ ...x, [k]: e.target.value }))} data-testid={`gdpr-field-${k}`} />
                                    : <Input value={editing?.[k] || ''} onChange={e => setEditing(x => ({ ...x, [k]: e.target.value }))} data-testid={`gdpr-field-${k}`} />}
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
                        <Button onClick={saveEntry} disabled={saving} data-testid="gdpr-save-btn">
                            {saving && <Loader2 size={13} className="mr-2 animate-spin" />} Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
